import React from 'react';
import { Box, CircularProgress, Collapse } from '@mui/material';
import { getActionBtnColor, getActionLabel, getIconPathForLabel, poppins } from '../helpers';
import { Patient, PatientTask } from '../types';

export const TaskRow: React.FC<{
    task: PatientTask;
    onDigerTedaviClick?: (task: PatientTask) => void;
    onQuickActionClick?: (task: PatientTask) => void;
    isQuickLoading?: boolean;
}> = ({ task, onDigerTedaviClick, onQuickActionClick, isQuickLoading }) => {
    const timeColor = task.status === 'expired' ? '#CA0003' : '#2196F3';
    const actionLabel = getActionLabel(task);
    const actionColors = getActionBtnColor(task);
    const isDisabled = task.status === 'applied';
    const canHandleClick =
        !isDisabled &&
        (
            task.type === 'digerTedavi' ||
            task.actionType === 'digerTedaviUygula' ||
            task.type === 'medicine' ||
            task.label === 'Kan İstekleri' ||
            task.label === 'Bakım Planları'
        );

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px', borderRadius: '8px', background: '#fff', border: '1px solid rgba(25, 111, 247, 0.10)', boxSizing: 'border-box' }}>
            <Box sx={{ width: 23, height: 23, borderRadius: '50%', background: 'rgba(25, 111, 247, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <img
                    src={getIconPathForLabel(task.label)}
                    alt={task.label}
                    style={{ width: 14, height: 14 }}
                    onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0'; }}
                />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <Box sx={{ fontFamily: poppins, fontSize: '12px', fontWeight: 500, color: '#1F2026', letterSpacing: '-0.02em', lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {task.label}
                    {task.procedure && (
                        <Box component="span" sx={{ fontWeight: 400, color: '#888', fontSize: '11px', ml: '4px' }}>
                            {' - '} {task.procedure}
                        </Box>
                    )}
                </Box>
                <Box sx={{ fontFamily: poppins, fontSize: '11px', fontWeight: 300, color: timeColor, textDecoration: 'underline', lineHeight: '16px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {task.time}
                    {task.endTime && <Box component="span" sx={{ textDecoration: 'none', color: '#AAA' }}>{' - '}{task.endTime}</Box>}
                    {task.location && <Box component="span" sx={{ color: '#AAA', textDecoration: 'none', ml: '4px' }}>({task.location})</Box>}
                </Box>
            </Box>

            <Box
                onClick={() => {
                    if (isDisabled) return;
                    if (onQuickActionClick && (task.typeId === 4 || task.typeId === 5)) {
                        onQuickActionClick(task);
                        return;
                    }
                    if (canHandleClick && onDigerTedaviClick) {
                        onDigerTedaviClick(task);
                    }
                }}
                sx={{
                    height: '24px',
                    borderRadius: '20px',
                    background: actionColors.bg,
                    border: `1px solid ${actionColors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    cursor: isDisabled || isQuickLoading ? 'default' : 'pointer',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    opacity: isDisabled ? 0.6 : isQuickLoading ? 0.7 : 1,
                    pointerEvents: isDisabled || isQuickLoading ? 'none' : 'auto',
                    '&:hover': { opacity: isDisabled ? 0.6 : isQuickLoading ? 0.7 : 0.8 },
                }}
            >
                {isQuickLoading ? (
                    <CircularProgress size={14} sx={{ color: actionColors.text }} />
                ) : (
                    <Box sx={{ fontFamily: poppins, fontSize: '10px', fontWeight: 500, color: actionColors.text }}>
                        {actionLabel}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export const PatientRow: React.FC<{
    patient: Patient;
    isExpanded: boolean;
    onToggle: () => void;
    onToggleFavorite: (patientId: string, currentIsFavorite: boolean) => void;
    onDigerTedaviClick?: (task: PatientTask) => void;
    onQuickActionClick?: (patientId: string, task: PatientTask) => void;
    quickActionLoadingTaskId?: string | null;
}> = ({ patient, isExpanded, onToggle, onToggleFavorite, onDigerTedaviClick, onQuickActionClick, quickActionLoadingTaskId }) => (
    <Box>
        <Box sx={{ height: '1px', background: '#EBEBEB' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: '#fff', '&:hover': { background: 'rgba(33,150,243,0.03)' } }}>
            <Box
                onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(patient.id, patient.isFavorite);
                }}
                sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: patient.isFavorite ? '#FFC107' : '#CCC', cursor: 'pointer', '&:hover': { color: '#FFCA28' } }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill={patient.isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
            </Box>

            <Box onClick={onToggle} sx={{ fontFamily: poppins, fontSize: '14px', fontWeight: 500, color: '#111432', letterSpacing: '-0.01em', lineHeight: '24px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                {patient.name}
            </Box>

            <Box onClick={onToggle} sx={{ display: 'flex', alignItems: 'center', color: '#555', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0, cursor: 'pointer' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </Box>
        </Box>

        <Collapse in={isExpanded} timeout={200}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 10px 10px', background: '#F6F6F6' }}>
                {patient.tasks.map((task) => (
                    <TaskRow
                        key={task.id}
                        task={task}
                        onDigerTedaviClick={onDigerTedaviClick}
                        onQuickActionClick={onQuickActionClick ? () => onQuickActionClick(patient.id, task) : undefined}
                        isQuickLoading={quickActionLoadingTaskId === task.id}
                    />
                ))}
            </Box>
        </Collapse>
    </Box>
);

export const getCategoryIcon = (label: string) => (
    <img
        src={getIconPathForLabel(label)}
        alt={label}
        style={{ width: 18, height: 18 }}
        onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0'; }}
    />
);

export const CategoryRow: React.FC<{
    label: string;
    tasks: Array<{ task: PatientTask; patient: Patient }>;
    isExpanded: boolean;
    onToggle: () => void;
    iconNode?: React.ReactNode;
    onTaskAction?: (task: PatientTask, patient: Patient) => void;
}> = ({ label, tasks, isExpanded, onToggle, iconNode, onTaskAction }) => (
    <div style={{ alignSelf: 'stretch', paddingTop: isExpanded ? 10 : 15, paddingBottom: 15, paddingLeft: 10, paddingRight: 10, background: 'white', boxShadow: '0px 10px 50px rgba(81.81, 81.81, 81.81, 0.04)', borderRadius: 8, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 10, display: 'flex', cursor: 'pointer' }} onClick={onToggle}>
        <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 16, display: 'inline-flex' }}>
            <div style={{ flex: 1, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 2, display: 'inline-flex' }}>
                <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 5, display: 'inline-flex' }}>
                    <div style={{ width: 30, height: 32, paddingLeft: 3, paddingRight: 3, paddingTop: 4, paddingBottom: 4, background: 'rgba(25, 111, 247, 0.10)', borderRadius: 8, justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                        {iconNode}
                    </div>
                    <div style={{ minWidth: 120, color: '#111432', fontSize: 14, fontFamily: 'Poppins', fontWeight: isExpanded ? '600' : '400', lineHeight: '24px', wordWrap: 'break-word' }}>
                        {label}
                    </div>
                    <div style={{ width: 24, height: 24, paddingLeft: 6, paddingRight: 6, paddingTop: 3, paddingBottom: 3, background: '#2196F3', borderRadius: 12, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'inline-flex' }}>
                        <div style={{ textAlign: 'center', color: 'white', fontSize: 10, fontFamily: 'Poppins', fontWeight: '700', wordWrap: 'break-word' }}>{tasks.length}</div>
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', color: '#555', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </div>
        </div>

        <Collapse in={isExpanded} timeout={200} style={{ width: '100%' }}>
            <div style={{ width: '100%', borderRadius: 8, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 10, display: 'flex', marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
                {tasks.map((ct) => (
                    <div key={ct.task.id} style={{ width: '100%', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                        <div style={{ flex: 1, paddingLeft: 6, paddingRight: 6, paddingTop: 8, paddingBottom: 8, background: 'white', borderRadius: 8, outline: '1px rgba(25, 111, 247, 0.10) solid', outlineOffset: '-1px', justifyContent: 'space-between', alignItems: 'center', gap: 5, display: 'flex' }}>
                            <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 5, display: 'flex' }}>
                                <div style={{ flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 5, display: 'inline-flex' }}>
                                    <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 5, display: 'inline-flex' }}>
                                        <div style={{ minWidth: 88, display: 'flex', flexDirection: 'column', color: 'black', fontSize: 12, fontFamily: 'Poppins', fontWeight: '400', lineHeight: '18px', wordWrap: 'break-word' }}>
                                            {ct.patient.name}
                                        </div>
                                        <div style={{ height: 24, paddingLeft: 10, paddingRight: 10, paddingTop: 4, paddingBottom: 4, overflow: 'hidden', borderRadius: 20, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'inline-flex' }}>
                                            <div style={{ justifyContent: 'center', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                                                <div style={{ color: ct.task.status === 'expired' ? '#CA0003' : '#2979C3', fontSize: 12, fontFamily: 'Poppins', fontStyle: 'italic', fontWeight: '300', textDecoration: 'underline', lineHeight: '18px', wordWrap: 'break-word' }}>
                                                    {ct.task.time}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {ct.task.procedure && (
                                        <div style={{ fontSize: 10, fontFamily: 'Poppins', color: '#888', lineHeight: '14px' }}>
                                            {ct.task.procedure}{ct.task.location ? ` - ${ct.task.location}` : ''}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div
                                style={{ cursor: ct.task.status === 'applied' ? 'default' : 'pointer', height: 24, paddingLeft: 10, paddingRight: 10, paddingTop: 4, paddingBottom: 4, background: getActionBtnColor(ct.task).bg, overflow: 'hidden', borderRadius: 20, outline: `1px ${getActionBtnColor(ct.task).border} solid`, outlineOffset: '-1px', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'inline-flex', whiteSpace: 'nowrap' }}
                                onClick={() => {
                                    if (ct.task.status === 'applied') return;

                                    const canHandleClick =
                                        ct.task.type === 'digerTedavi' ||
                                        ct.task.actionType === 'digerTedaviUygula' ||
                                        ct.task.type === 'medicine' ||
                                        ct.task.label === 'Kan İstekleri' ||
                                        ct.task.label === 'Bakım Planları' ||
                                        ct.task.label === 'Ameliyathane' ||
                                        ct.task.label === 'Tetkik Hazırlık';

                                    if (canHandleClick && onTaskAction) {
                                        onTaskAction(ct.task, ct.patient);
                                    }
                                }}
                            >
                                <div style={{ justifyContent: 'center', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                                    <div style={{ color: getActionBtnColor(ct.task).text, fontSize: 10, fontFamily: 'Poppins', fontWeight: '500', lineHeight: '22px', wordWrap: 'break-word' }}>
                                        {getActionLabel(ct.task)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Collapse>
    </div>
);
